import { useEffect, useState, useMemo } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Building2, Loader2, Search, ChevronDown, MapPin, Phone, Mail, Users, CheckCircle2, Network } from "lucide-react";

export default function AgenzieAbilitate() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState({});

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/agencies");
        setData(data);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Errore caricamento agenzie");
      } finally { setLoading(false); }
    })();
  }, []);

  const agencies = data?.agencies || [];
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return agencies;
    return agencies.filter((a) =>
      [a.name, a.referente, a.email, ...(a.provinces || []), ...(a.regions || [])]
        .filter(Boolean).join(" ").toLowerCase().includes(s));
  }, [agencies, q]);

  const totInstallers = agencies.reduce((n, a) => n + (a.installers_total || 0), 0);
  const totAttivi = agencies.reduce((n, a) => n + (a.installers_attivi || 0), 0);

  return (
    <div className="space-y-6" data-testid="agenzie-abilitate-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading font-bold text-2xl flex items-center gap-2">
            <Building2 className="h-6 w-6 text-primary" strokeWidth={1.5} /> Agenzie Abilitate
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Agenzie abilitate all'azione sulla divisione <b>Chiavi in Mano</b>, con i relativi installatori partner collegati.
          </p>
          <p className="text-xs text-primary mt-1 flex items-center gap-1">
            <Network className="h-3 w-3" /> Fonte: <b>rete.torri.solar</b>
            {data?.source === "derived_from_installers" && (
              <span className="text-muted-foreground"> — in attesa dell'endpoint agenzie, elenco ricavato dagli installatori sincronizzati</span>
            )}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3" data-testid="agenzie-stats">
        {[
          ["Agenzie abilitate", agencies.length, "text-primary"],
          ["Installatori collegati", totInstallers, "text-foreground"],
          ["Installatori attivi", totAttivi, "text-success"],
        ].map(([label, val, cls]) => (
          <Card key={label} className="p-4">
            <div className={`text-2xl font-bold ${cls}`}>{val}</div>
            <div className="text-xs text-muted-foreground mt-1">{label}</div>
          </Card>
        ))}
      </div>

      <div className="relative max-w-sm">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca agenzia, referente, provincia…"
          className="pl-9" data-testid="agenzie-search" />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin mr-2" /> Caricamento…
        </div>
      ) : filtered.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground" data-testid="agenzie-empty">
          Nessuna agenzia abilitata trovata.
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((a) => {
            const isOpen = !!open[a.id];
            return (
              <Card key={a.id} className="overflow-hidden" data-testid={`agenzia-card-${a.id}`}>
                <button
                  onClick={() => setOpen((o) => ({ ...o, [a.id]: !o[a.id] }))}
                  className="w-full text-left p-4 flex items-start justify-between gap-4 hover:bg-secondary/40 transition-colors"
                  data-testid={`agenzia-toggle-${a.id}`}
                >
                  <div className="min-w-0 space-y-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-base">{a.name}</span>
                      {a.chiavi_in_mano && (
                        <Badge variant="outline" className="border-success/40 text-success text-[11px] font-normal">
                          <CheckCircle2 className="h-3 w-3 mr-1" /> Chiavi in mano
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-x-4 gap-y-1 flex-wrap text-xs text-muted-foreground">
                      {a.referente && <span className="flex items-center gap-1"><Users className="h-3 w-3" /> {a.referente}</span>}
                      {a.sede_city && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {a.sede_city}</span>}
                      {a.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {a.phone}</span>}
                      {a.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> {a.email}</span>}
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {(a.regions || []).map((r) => (
                        <Badge key={r} variant="secondary" className="text-[11px] font-normal">{r}</Badge>
                      ))}
                      {(a.provinces || []).map((p) => (
                        <Badge key={p} variant="outline" className="text-[11px] font-normal">{p}</Badge>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="text-right">
                      <div className="text-sm font-semibold">{a.installers_total} installatori</div>
                      <div className="text-xs text-success">{a.installers_attivi} attivi</div>
                    </div>
                    <ChevronDown className={`h-5 w-5 text-muted-foreground transition-transform ${isOpen ? "rotate-180" : ""}`} />
                  </div>
                </button>
                {isOpen && (
                  <div className="border-t border-border" data-testid={`agenzia-installers-${a.id}`}>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Installatore</TableHead>
                          <TableHead>Comune</TableHead>
                          <TableHead>Prov.</TableHead>
                          <TableHead>Stato</TableHead>
                          <TableHead>Contatti</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {a.installers.map((i, idx) => (
                          <TableRow key={idx} data-testid={`agenzia-${a.id}-installer-${idx}`}>
                            <TableCell className="font-medium">{i.ragione_sociale}</TableCell>
                            <TableCell>{i.comune || "—"}</TableCell>
                            <TableCell>{i.provincia || "—"}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className={`text-[11px] font-normal ${i.attivo ? "border-success/40 text-success" : "border-muted-foreground/40 text-muted-foreground"}`}>
                                {i.attivo ? "Attivo" : "Inattivo"}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-xs text-muted-foreground">
                              {[i.telefono, i.email].filter(Boolean).join(" · ") || "—"}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
